import { ensurePostHog, getPostHog, queuePostHogCall } from './posthog-lazy';

// Firebase is dynamically imported in syncOptOutFromFirestore / setOptOut so it
// never lands in the boot graph. Without this, the marketing chunk would pull
// in vendor-firebase (~128 KB gz) on every landing page load just because
// applyStoredOptOut() ships from this module at startup. Boot only needs
// localStorage — posthog-js is also lazy-loaded via posthog-lazy.

/**
 * Per-user, cross-device session-recording opt-out.
 *
 * Three-layer model:
 *   1. GDPR cookie consent (handled by CookieBanner) — required floor; without
 *      it PostHog never initialises in the first place.
 *   2. Firestore flag on `users/{uid}.sessionRecordingOptOut` — sticks across
 *      devices. Source of truth.
 *   3. localStorage mirror (`rqio_sr_opt_out`) — read synchronously on init so
 *      replay never starts before the Firestore round-trip resolves.
 *
 * Call `applyStoredOptOut()` once at app boot (after `initAnalytics`).
 * Call `syncOptOutFromFirestore(uid)` once we know who the user is.
 * Call `setOptOut(uid, value)` from the Settings toggle.
 */

const LS_KEY = 'rqio_sr_opt_out';
const TOAST_DISMISSED_KEY = 'rqio_sr_replay_toast_dismissed';

function safeLocalGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked — best-effort */
  }
}

function safeLocalRemove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function optOutNow() {
  const ph = getPostHog();
  if (ph) {
    try { ph.opt_out_capturing(); } catch { /* ignore */ }
    return;
  }
  queuePostHogCall({ kind: 'opt_out_capturing' });
  // Kick off the lazy load so the queued call drains.
  void ensurePostHog().catch(() => { /* ignore */ });
}

function optInNow() {
  const ph = getPostHog();
  if (ph) {
    try { ph.opt_in_capturing(); } catch { /* ignore */ }
    return;
  }
  queuePostHogCall({ kind: 'opt_in_capturing' });
  void ensurePostHog().catch(() => { /* ignore */ });
}

/** Returns true if the user has opted out per the local mirror. Synchronous. */
export function isOptedOutLocally(): boolean {
  return safeLocalGet(LS_KEY) === '1';
}

/**
 * Applies any stored opt-out to PostHog immediately. Call at boot so a refresh
 * never accidentally starts a replay for a user who previously opted out.
 */
export function applyStoredOptOut(): void {
  if (!isOptedOutLocally()) return;
  optOutNow();
}

/**
 * Reads `users/{uid}.sessionRecordingOptOut` and mirrors it to localStorage.
 * Call once after sign-in. Safe to call repeatedly — last write wins.
 */
export async function syncOptOutFromFirestore(uid: string): Promise<boolean> {
  if (!uid) return false;
  try {
    const [{ doc, getDoc }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('./firebase'),
    ]);
    const snap = await getDoc(doc(db, 'users', uid));
    const optedOut = snap.exists() && snap.data().sessionRecordingOptOut === true;
    if (optedOut) {
      safeLocalSet(LS_KEY, '1');
      optOutNow();
    } else {
      safeLocalRemove(LS_KEY);
      optInNow();
    }
    return optedOut;
  } catch {
    // On Firestore error, fall back to the local mirror — fail closed if locally set.
    return isOptedOutLocally();
  }
}

/**
 * Persist + apply the opt-out preference. Writes to Firestore so the choice
 * sticks across devices; mirrors locally so init is synchronous.
 */
export async function setOptOut(uid: string, optedOut: boolean): Promise<void> {
  if (optedOut) {
    safeLocalSet(LS_KEY, '1');
    optOutNow();
  } else {
    safeLocalRemove(LS_KEY);
    optInNow();
  }
  if (!uid) return;
  try {
    const [{ doc, setDoc }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('./firebase'),
    ]);
    await setDoc(
      doc(db, 'users', uid),
      { sessionRecordingOptOut: optedOut },
      { merge: true },
    );
  } catch {
    // Persistence failed — local state still applied, will re-sync next session.
  }
}

/** Whether the "session recording is on" one-time toast has been dismissed. */
export function isReplayToastDismissed(): boolean {
  return safeLocalGet(TOAST_DISMISSED_KEY) === '1';
}

/** Mark the toast as dismissed so it never re-shows for this browser profile. */
export function dismissReplayToast(): void {
  safeLocalSet(TOAST_DISMISSED_KEY, '1');
}
